import React from 'react';
import { makeStyles } from '@material-ui/core';
import { alpha } from '@material-ui/core/styles';
import LaunchIcon from '@mui/icons-material/Launch';
import { Card, CardContent, Button, Typography, Box } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useApi } from '@backstage/core-plugin-api';
import { analyticsApiRef } from '@backstage/core-plugin-api';

const useStyles = makeStyles(theme => ({
  card: {
    backgroundColor: theme.palette.background.paper,
    color: theme.palette.text.primary,
    minHeight: '300px',
    display: 'flex',
    flexDirection: 'column',
    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
    borderRadius: '12px',
    position: 'relative',
    boxShadow: theme.shadows[2],
    margin: theme.spacing(1),
    flex: 1,
  },
  externalLinkIcon: {
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(1),
    color: theme.palette.primary.main,
    cursor: 'pointer',
  },
  cardContent: {
    padding: theme.spacing(2),
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    overflow: 'hidden',
  },
  title: {
    fontSize: '1rem',
    fontWeight: 'bold',
    color: theme.palette.primary.main,
    overflowWrap: 'break-word',
    wordBreak: 'break-all',
    marginBottom: theme.spacing(1),
  },
  titleContainer: {
    marginBottom: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  },
  descriptionContainer: {
    overflow: 'auto',
    maxHeight: '120px',
    flexGrow: 1,
    marginBottom: theme.spacing(1),
  },
  description: {
    fontSize: '0.85rem',
    lineHeight: 1.4,
    color: theme.palette.text.secondary,
  },
  tagsContainer: {
    display: 'flex',
    gap: theme.spacing(0.5),
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap',
  },
  tag: {
    fontSize: '0.75rem',
    color: theme.palette.primary.main,
    backgroundColor: alpha(theme.palette.primary.main, 0.05),
    padding: theme.spacing(0.5, 1),
    borderRadius: '16px',
    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
  },
  viewProductButton: {
    fontSize: '0.75rem',
    padding: theme.spacing(1),
    borderRadius: '8px',
    textTransform: 'none',
    backgroundColor: theme.palette.primary.main,
    color: 'white',
    width: '100%',
    '&:hover': {
      backgroundColor: '#162d5c',
    },
  },
}));

export interface AppCardProps {
  id: number;
  title: string;
  description: string;
  external_link?: string;
  docs?: string;
  items?: string[];
  type: string;
  team?: string;
  is_plugin: boolean;
  product_key?: string;
  usageCount?: string | number;
}

export const AppCard: React.FC<AppCardProps> = ({
  id,
  title,
  description,
  external_link,
  docs,
  items,
  type,
  team,
  usageCount,
}) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const analytics = useApi(analyticsApiRef);

  const emitAssetClick = () => {
    try {
      analytics.captureEvent({
        action: 'asset_click',
        subject: title,
        context: {
          pluginId: 'home-page',
          extension: 'AppCard',
          routeRef: 'marketplace',
        },
        attributes: {
          asset_id: id,
          title,
          type,
          ...(team ? { team } : {}),
        },
      });
    } catch (error) {
      // Fire-and-forget telemetry — analytics must not block navigation
      // eslint-disable-next-line no-console
      console.warn('Asset click tracking failed', error);
    }
  };

  return (
    <Card className={classes.card}>
      {external_link && (
        <LaunchIcon
          fontSize="small"
          className={classes.externalLinkIcon}
          aria-label="Open external link"
          onClick={() => {
            emitAssetClick();
            window.open(external_link, '_blank', 'noopener,noreferrer');
          }}
        />
      )}
      <CardContent className={classes.cardContent}>
        <Box>
          <Box className={classes.tagsContainer}>
            {team && (
              <Box className={classes.tag}>
                {team}
              </Box>
            )}
            {type && (
              <Box className={classes.tag}>
                {type}
              </Box>
            )}
          </Box>
          <Box className={classes.titleContainer}>
            <Typography className={classes.title}>{title}</Typography>
          </Box>
          <Box className={classes.descriptionContainer}>
            <Typography className={classes.description}>
              {description}
            </Typography>
            {items && items.length > 0 && (
              <Box sx={{ mt: 1 }}>
                {items.map((item, index) => (
                  <Typography
                    key={index}
                    sx={{
                      fontSize: '0.8rem',
                      color: 'text.secondary',
                      mb: 0.5,
                      '&:before': { content: '"• "' },
                    }}
                  >
                    {item}
                  </Typography>
                ))}
              </Box>
            )}
          </Box>
        </Box>
        {usageCount !== undefined && usageCount !== '' && usageCount !== 0 && (
          <Typography
            sx={{
              color: '#5AAA95',
              textAlign: 'end',
              fontSize: '0.85rem',
              fontWeight: 600,
              marginBottom: 1,
            }}
          >
            Accessed {usageCount} times
          </Typography>
        )}
        <Button
          className={classes.viewProductButton}
          onClick={() => {
            emitAssetClick();
            if (docs) {
              navigate(docs);
            } else if (external_link) {
              window.open(external_link, '_blank', 'noopener,noreferrer');
            }
          }}
          disabled={!docs && !external_link}
        >
          Explore
        </Button>
      </CardContent>
    </Card>
  );
};
